import React from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink } from "lucide-react";

interface TopIdea {
  id: string;
  title: string;
  deck_count: number;
  reply_count: number;
  view_count: number;
  milestone_count: number;
  last_activity: string;
  score: number;
}

interface MetricsTopIdeasProps {
  ideas: TopIdea[];
}

export default function MetricsTopIdeas({ ideas }: MetricsTopIdeasProps) {
  if (!ideas || ideas.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No ideas yet. Create your first idea to see it here!
      </div>
    );
  }

  return (
    <div className="rounded-md border print:text-xs">
      <Table>
        <TableHeader>
          <TableRow className="print:text-[10px]">
            <TableHead className="print:py-1">Title</TableHead>
            <TableHead className="text-center print:py-1">Decks</TableHead>
            <TableHead className="text-center print:py-1">Replies</TableHead>
            <TableHead className="text-center print:py-1">Views</TableHead>
            <TableHead className="text-center print:py-1">Milestones</TableHead>
            <TableHead className="print:py-1">Last Activity</TableHead>
            <TableHead className="print:hidden"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ideas.map((idea, index) => (
            <TableRow key={idea.id} className="print:text-[10px]">
              <TableCell className="font-medium print:py-1">
                <div className="flex items-center gap-2">
                  {index < 3 && (
                    <Badge
                      variant="secondary"
                      className="w-6 h-6 flex items-center justify-center print:w-4 print:h-4 print:text-[8px]"
                    >
                      {index + 1}
                    </Badge>
                  )}
                  <span className="truncate max-w-[200px] print:max-w-[150px]">
                    {idea.title}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-center print:py-1">
                <Badge variant="outline" className="print:text-[8px] print:px-1">
                  {idea.deck_count}
                </Badge>
              </TableCell>
              <TableCell className="text-center print:py-1">
                <Badge
                  variant="outline"
                  className={`print:text-[8px] print:px-1 ${
                    idea.reply_count > 0 ? "bg-green-500/10 text-green-500" : ""
                  }`}
                >
                  {idea.reply_count}
                </Badge>
              </TableCell>
              <TableCell className="text-center print:py-1">
                <Badge variant="outline" className="print:text-[8px] print:px-1">
                  {idea.view_count}
                </Badge>
              </TableCell>
              <TableCell className="text-center print:py-1">
                <Badge
                  variant="outline"
                  className={`print:text-[8px] print:px-1 ${
                    idea.milestone_count > 0 ? "bg-blue-500/10 text-blue-500" : ""
                  }`}
                >
                  {idea.milestone_count}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground print:py-1 print:text-[10px]">
                {formatDistanceToNow(new Date(idea.last_activity), {
                  addSuffix: true,
                })}
              </TableCell>
              <TableCell className="print:hidden">
                <Link to={`/idea/${idea.id}`}>
                  <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary" />
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}